/**
 * hexToRgb - Converts a hex code to an object with RGB values
 */
export function hexToRgb (hexCode: string): { r: number, g: number, b: number } {
  if (hexCode.startsWith('#')) {
    hexCode = hexCode.slice(1)
  }

  let r = parseInt(hexCode.substring(0, 2), 16)
  let g = parseInt(hexCode.substring(2, 4), 16)
  let b = parseInt(hexCode.substring(4, 6), 16)

  r = r < 255 ? r : 255
  g = g < 255 ? g : 255
  b = b < 255 ? b : 255

  return {
    r,
    g,
    b
  }
}

/**
 * rgbToHex - Converts RGB values to a hex color string
 */
export function rgbToHex (r: number, g: number, b: number): string {
  r = r > 255 ? 255 : r
  g = g > 255 ? 255 : g
  b = b > 255 ? 255 : b

  const rr = r.toString(16).padStart(2, '0')
  const gg = g.toString(16).padStart(2, '0')
  const bb = b.toString(16).padStart(2, '0')

  return `#${rr}${gg}${bb}`
}

/**
 * lightenColor - Lightens a color by a decimal percentage, where 1 is full lightness, -1 is full darkness.
 */
export function lightenColor (hexCode: string, lightenPct: number): string {
  const lightenAmount = 1 + lightenPct

  let {
    r,
    g,
    b
  } = hexToRgb(hexCode)

  r = Math.round(r * lightenAmount)
  g = Math.round(g * lightenAmount)
  b = Math.round(b * lightenAmount)

  return rgbToHex(r, g, b)
}

/**
 * getColorTransparent - Takes a hex code and a transparency amount and returns an rgba() string
 */
export function getColorTransparent (hexCode: string, alpha: number): string {
  const {
    r,
    g,
    b
  } = hexToRgb(hexCode)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * getColorParts - Extracts the color and opacity from an rgba() string, converting RGB to hex
 */
export function getColorParts (color: string): { color: string, opacity?: number } {
  if (!color.includes('rgba(')) {
    return { color }
  }
  const parts: string[] = color.slice(5, color.length - 1).split(',')
  const alphaPart: number = parseFloat(parts[3])
  const colorOnly: string = rgbToHex(
    parseInt(parts[0]),
    parseInt(parts[1]),
    parseInt(parts[2])
  )

  return {
    color: colorOnly,
    opacity: alphaPart
  }
}
